import { EventCard } from "@/components/portal/accreditation/EventCard";
import { useQuery, useMutation } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "@/dashboardQueries";
import { OrderPay } from "@/models/accreditation"; // Assuming this is the correct path for the OrderPay type
import { PaymentDetails } from "@/components/PaymentDetails";
import { PaymentIntent, SetupIntent } from "@stripe/stripe-js";
import { OrderStatus } from "@/models/Dashboard";
import { formatNumberToMoney } from "@/utils/cart";
import { ChevronDown, ChevronUp, InfoIcon } from "lucide-react";
import { useState } from "react";
import successAnimation from "../../assets/successAnimation.json";
import Lottie from "lottie-react";

export const Pay = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [showDetails, setShowDetails] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();

  const {
    data: orderData,
    isLoading,
    isError,
  } = useQuery<OrderPay>(
    ["orderDetails", orderId],
    () =>
      axios
        .get<OrderPay>(`${BASE_URL}/accreditation/order/${orderId}/pay`)
        .then((res) => res.data),
    {
      enabled: !!orderId, // Only fetch if orderId exists
      retry: false, // Disable retries for 404 errors
      onError: (error) => {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          navigate("/not-found"); // Redirect if 404
        }
      },
    }
  );

  const paymentSuccessMutation = useMutation(
    (paymentIntentId: string) =>
      axios.put(
        `${BASE_URL}/accreditation/order/${orderId}/pay?payment_intent_id=${paymentIntentId}`
      ),
    {
      onSuccess: () => {
        console.log("Payment successfully recorded.");
        setIsSuccess(true);
      },
      onError: (error) => {
        console.error("Error recording payment:", error);
      },
    }
  );

  const handlePaymentSuccess = (paymentIntent: PaymentIntent | SetupIntent) => {
    if ("id" in paymentIntent) {
      paymentSuccessMutation.mutate(paymentIntent.id);
    } else {
      console.error("Invalid payment intent:", paymentIntent);
    }
  };

  if (!orderId) {
    navigate("/not-found"); // Redirect if orderId is missing
    return null;
  }

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error loading order details.</div>;
  }

  if (!orderData?.event) {
    return <div>No event data available.</div>;
  }

  const totals = orderData.tickets?.reduce(
    (acc, ticket) => acc + parseFloat(ticket.price),
    0
  );

  const feeTotal = orderData.tickets?.reduce(
    (acc, ticket) => acc + parseFloat(ticket.fee),
    0
  );

  const Cart = () => {
    return (
      <>
        {orderData?.tickets?.map((ticket) => (
          <div className="w-full">
            <div className="flex font-medium">
              <div>{ticket.name}</div>
              <div className="flex-1 justify-end flex">
                {formatNumberToMoney(ticket.price)}
              </div>
            </div>
            <div className="flex text-sm dark:text-zinc-600 text-zinc-400">
              <div>x1, Once off</div>
              <div className="flex-1 justify-end flex">
                {formatNumberToMoney(ticket.price)} each
              </div>
            </div>
          </div>
        ))}

        <div className="border-y dark:border-white/10 py-4 mt-4">
          <div className="flex font-medium">
            <div>Subtotal</div>
            <div className="flex-1 flex justify-end">
              {formatNumberToMoney(totals)}
            </div>
          </div>
          {/* <div className="flex mt-2 dark:text-zinc-500 text-zinc-400">
            <div className="flex items-center">
              <div>Fees</div>
              <InfoIcon className="inline-block h-3.5 w-3.5 ml-1.5 text-sm mt-0.5" />
            </div>
            <div className="flex-1 flex justify-end">
              {formatNumberToMoney(feeTotal)}
            </div>
          </div> */}
        </div>

        <div className=" pt-4">
          <div className="flex font-medium">
            <div>Total due today</div>
            <div className="flex-1 flex justify-end">
              {formatNumberToMoney(orderData.total)}
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="dark:bg-dark-primary dark:text-gray-200 font-sans">
      <div className="min-h-screen flex justify-center items-center px-4">
        <div className="lg:flex max-w-5xl w-full items-center">
          {orderData.status === OrderStatus.PENDING &&
            orderData.tickets &&
            !isSuccess && (
              <div className="w-1/2 hidden lg:block items-center pr-28">
                <Cart />
              </div>
            )}
          <div
            className={`${
              isSuccess ? "w-full" : "lg:w-1/2 w-full"
            } flex justify-center`}
          >
            <div>
              <EventCard event={orderData.event}>
                {isSuccess ? (
                  <div>
                    <div className="text-center">
                      <div className="w-full flex justify-center">
                        <div className="w-36 h-36">
                          <Lottie
                            animationData={successAnimation}
                            loop={false}
                          />
                        </div>
                      </div>
                      <h3 className="mt-4 font-semibold text-xl">
                        Payment successful
                      </h3>
                      <p className="text-gray-400 text-sm mt-1.5">
                        Thank you for your payment!
                      </p>
                      {/* todo: if receipt then say you'll be sent a receipt, if not, then it should show your tickets */}
                    </div>
                  </div>
                ) : (
                  <>
                    {orderData.status === OrderStatus.SUCCESS ? (
                      <div className="text-center">
                        This link has already been used for payment, thank you!
                      </div>
                    ) : (
                      <>
                        <div className="block lg:hidden">
                          {showDetails ? (
                            <div className="mb-4">
                              <div className="flex justify-end mb-2 text-zinc-400">
                                <div
                                  className="flex text-sm flex items-center cursor-pointer"
                                  onClick={() => setShowDetails(!showDetails)}
                                >
                                  <p className="underline decoration-dotted mr-1">
                                    Close
                                  </p>
                                  <ChevronUp className="h-4 w-4" />
                                </div>
                              </div>
                              <Cart />
                            </div>
                          ) : (
                            <div className="text-center mb-4 -mt-3">
                              <div>
                                <h1 className="text-2xl text-zinc-400">
                                  Pay{" "}
                                  <span className="font-semibold">
                                    {formatNumberToMoney(orderData.total)}
                                  </span>
                                </h1>
                              </div>
                              <div className="items-center justify-center flex mt-4">
                                <button
                                  type="button"
                                  onClick={() => setShowDetails(!showDetails)}
                                  className="rounded-md dark:bg-white/10 bg-black hover:bg-black/75 px-3 py-2 text-sm font-semibold text-white shadow-sm dark:hover:bg-white/20 flex items-center"
                                >
                                  View Details{" "}
                                  <ChevronDown className="w-4 h-4 ml-2" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        <PaymentDetails
                          total={parseFloat(orderData.total)}
                          stripeAccountId={
                            orderData?.event.organisation.accountId
                          }
                          clientSecret={orderData.gatewayId}
                          buttonText="Pay and claim ticket"
                          onPaymentSuccess={handlePaymentSuccess} // Pass the handler
                        />
                      </>
                    )}
                  </>
                )}
              </EventCard>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
